import React, { useEffect, useState } from 'react';
import InfoDialog from '../../../components/shared/InfoDialog';
import { useTranslation } from 'react-i18next';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Stack,
  Typography
} from '@mui/material';
import { IconCaretDown } from '@tabler/icons-react';
import CusCheckbox from '../../../components/material-ui/CusCheckbox';
import CusLabelSelect from '../../../components/material-ui/CusLabelSelect';
import { TEST_MODE } from '../../../enums/enums';
import { API_USERS } from '../../../api/users';
import { mockRight, mockTemplate } from '../../../enums/mock';
import useToast from '../../../hooks/useToast';

const infoById = {
  agent: { label: 'Agent', order: 0 },
  partner: { label: 'Partner', order: 0 },
  manager: { label: 'Manager', order: 0 },
  system: { label: 'System rights', order: 0 },
  group: { label: 'Group management', order: 0 },
  user: { label: 'User management', order: 0 },
  view: { label: 'Visibility settings', order: 0 },
  sysChangeSelfPsword: { label: 'Change own password', order: 0 },
  sysViewReports: { label: 'View reports', order: 1 },
  sysSiteAccess: { label: 'Access to the admin panel', order: 2 },
  sysInputInvoices: { label: 'Viewing requests for replenishment', order: 3 },
  sysInputGateRaw: { label: 'View payment gateway deposit responses', order: 4 },
  sysViewOutputInvoices: { label: 'View withdrawal accounts list', order: 5 },
  sysOutputInvoices: { label: 'Make player payouts', order: 6 },
  sysOutputGateRaw: { label: 'View payment gateway payout responses', order: 7 },
  sysReportProfit: { label: 'Get income report', order: 8 },
  sysReportGroup: { label: 'Get group report', order: 9 },
  sysGameListEdit: { label: 'Edit games list', order: 10 },
  sysCasinoSettings: { label: 'Manage casino settings', order: 11 },
  sysGameAnalytics: { label: 'Games analytics', order: 12 },
  sysCryptoGateAccess: { label: 'Crypto gateway access', order: 13 },
  sysTrafficStatistics: { label: 'View traffic statistics', order: 14 },
  sysChangeUserBalance: { label: 'Change user balance', order: 15 },
  grpAccessSubgroups: { label: 'Access to subgroups', order: 0 },
  grpMailingListControl: { label: 'Mailing management', order: 0 },
  grpChangeGroupOptions: { label: 'Change group settings', order: 1 },
  grpEditGroup: { label: 'Edit group', order: 2 },
  grpRegisterGroup: { label: 'Register group', order: 3 },
  grpDeleteGroup: { label: 'Delete group', order: 4 },
  grpChangeGroupLimit: { label: 'Changing the group bank limit', order: 5 },
  grpLockGroup: { label: 'Block groups', order: 6 },
  grpViewFeedback: { label: 'View counters and RTP %', order: 7 },
  grpMoveGroup: { label: 'Move groups', order: 8 },
  grpJackpotActions: { label: 'Jackpot operations', order: 9 },
  grpBonusControl: { label: 'Bonus management', order: 10 },
  grpRefcodeControl: { label: 'Refcode management', order: 11 },
  grpBannerControl: { label: 'Banner management', order: 12 },
  usrBalanceReplenishment: { label: 'Creating a Deposit Request by a Player', order: 0 },
  usrPayout: { label: 'Creating a Payout Request by a Player', order: 1 },
  usrRegisterUser: { label: 'Register users', order: 2 },
  usrInstantPayouts: { label: 'Instant payouts', order: 3 },
  usrViewReferer: { label: 'View referer', order: 4 },
  usrDeleteUsers: { label: 'Delete users', order: 5 },
  usrTransferUsers: { label: 'Move users', order: 6 },
  usrSetRightsAccess: { label: 'Set access rights', order: 7 },
  usrEditUser: { label: 'Edit users', order: 8 },
  usrChangeStatus: { label: 'Change user status', order: 9 },
  usrViewFeedback: { label: 'View counters and RTP %', order: 10 },
  usrChangeUserPsword: { label: 'Change passwords', order: 11 },
  usrViewLog: { label: 'View logs', order: 12 },
  viewOfflineUsers: { label: 'Offline users', order: 0 },
  viewFinanceStatistics: { label: 'Viewing Financial Statistics', order: 0 },
  viewGameLogins: { label: 'Game logins', order: 1 },
  viewManagers: { label: 'Managers', order: 2 },
  viewPartners: { label: 'Partners', order: 3 },
  viewAgents: { label: 'Agents', order: 4 }
};

export default function AccessRights({ open, closeModal, user }) {
  const { t } = useTranslation();
  const { successToast } = useToast();
  const title = `${t('User access rights')} ${user.login}`;
  // const [selectRight, setSelectRight] = useState([]);
  const [templates, setTemplates] = useState({});
  const [rights, setRights] = useState({});
  const [myRights, setMyRights] = useState({});
  const [selectTemplate, setSelectTemplate] = useState('no');

  const userId = user.userId;

  const templateOptions = Object.keys(templates).map((el) => ({
    label: infoById[el]?.label,
    value: el
  }));
  templateOptions.unshift({ label: 'Do not use', value: 'no' });

  const getLabelById = (id) => infoById[id]?.label || id;
  const getOrderId = (id) => infoById[id]?.order || 0;

  useEffect(() => {
    setTemplates({});
    setRights({});
    setMyRights({});
    setSelectTemplate('no');

    if (!userId) return;
    if (!open) return;
    if (TEST_MODE) {
      setTemplates(mockTemplate);
      setRights(mockRight);
      return;
    }

    API_USERS.info.rights
      .get({ userId })
      .then((e) => {
        const data = e.data;
        setTemplates(data.default);
        setRights(data.rights);
        setMyRights(data.rights);

        // const trueKeys = Object.values(data.rights).flatMap((cat) =>
        //   Object.keys(cat).filter((key) => cat[key] === true)
        // );

        // setSelectRight(trueKeys);
      })
      .catch(console.error);
  }, [userId, open]);

  useEffect(() => {
    if (selectTemplate == 'no') {
      setRights(myRights);
      return;
    }
    if (!templates[selectTemplate]) return;

    const info = templates[selectTemplate];
    setRights(info);

    // const trueKeys = Object.values(info).flatMap((cat) =>
    //   Object.keys(cat).filter((key) => cat[key] === true)
    // );

    // setSelectRight(trueKeys);
  }, [selectTemplate]);

  const onSaveClick = () => {
    API_USERS.info.rights
      .set({ userId, rights })
      .then((e) => {
        successToast('Information updated successfully');
        closeModal();
      })
      .catch(console.error);
  };

  return (
    <InfoDialog
      open={open}
      onClose={closeModal}
      title={title}
      maxWidth="sm"
      onSaveClick={onSaveClick}
      contentSx={{ height: '90svh' }}>
      <Stack gap={2}>
        <Stack direction={'row'}>
          <CusLabelSelect
            label={'Template selection'}
            isRow
            value={selectTemplate}
            options={templateOptions}
            onChange={setSelectTemplate}
          />
        </Stack>

        <Box>
          {Object.keys(rights).map((keyRight, indx) => {
            const title = getLabelById(keyRight);
            const block = rights[keyRight];
            const blockKeys = Object.keys(block);

            return (
              <Accordion key={keyRight} defaultExpanded={!indx}>
                <AccordionSummary expandIcon={<IconCaretDown />}>
                  <Typography fontWeight={600}>{t(title)}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack gap={0}>
                    {blockKeys.map((fieldId) => (
                      <Stack order={getOrderId(fieldId)} key={keyRight + fieldId}>
                        <CusCheckbox
                          label={`${t(getLabelById(fieldId))}`}
                          // checked={selectRight.includes(fieldId)}
                          checked={block[fieldId]}
                          onChange={() => {
                            setRights((prev) => {
                              const prevInfo = JSON.parse(JSON.stringify(prev));
                              prevInfo[keyRight][fieldId] = !prevInfo[keyRight][fieldId];

                              return prevInfo;
                            });
                            // setSelectRight((prev) =>
                            //   prev.includes(fieldId)
                            //     ? prev.filter((el) => el !== fieldId)
                            //     : [...prev, fieldId]
                            // );
                          }}
                        />
                      </Stack>
                    ))}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>
      </Stack>
    </InfoDialog>
  );
}
